import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import About from './pages/About';
import HowItWorks from './pages/HowItWorks';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import FootballAnalysisSoftware from './pages/FootballAnalysisSoftware';
import SoccerCoachingTools from './pages/SoccerCoachingTools';
import SoccerAnalysisSoftware from './pages/SoccerAnalysisSoftware';
import PostMatchAnalysisEngine from './pages/PostMatchAnalysisEngine';
import MatchEvidencePlatform from './pages/MatchEvidencePlatform';
import Products from './pages/Products';
import ProductPostMatch from './pages/ProductPostMatch';
import ProductFederation from './pages/ProductFederation';
import Insights from './pages/Insights';
import InsightDetail from './pages/InsightDetail';
import Dashboard from './pages/Dashboard';
import UploadMatch from './pages/UploadMatch';
import AnalysisReport from './pages/AnalysisReport';
import Matches from './pages/Matches';
import Reports from './pages/Reports';
import Teams from './pages/Teams';
import Players from './pages/Players';
import Settings from './pages/Settings';
import PlatformAdmin from './pages/PlatformAdmin';
import AdminLogin from './pages/AdminLogin';
import SharedReport from './pages/SharedReport';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Patterns from './pages/Patterns';
import MatchMemory from './pages/MatchMemory';
import FederationDashboard from './pages/federation/FederationDashboard';
import FederationPrograms from './pages/federation/FederationPrograms';
import FederationSubmissions from './pages/federation/FederationSubmissions';
import FederationFindings from './pages/federation/FederationFindings';
import FederationActions from './pages/federation/FederationActions';
import FederationObjectives from './pages/federation/FederationObjectives';
import FederationReviews from './pages/federation/FederationReviews';
import FederationReports from './pages/federation/FederationReports';
import FederationSettings from './pages/federation/FederationSettings';
import ChooseProduct from './pages/ChooseProduct';
import { AuthProvider, useAuth } from './utils/AuthContext';
import { orgCanAccessPostMatch } from './utils/productWorkspace';

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }
  return user ? children : <Navigate to="/login" />;
}

function PostMatchRoute({ children }) {
  const { org, loading } = useAuth();
  if (loading) return null;
  return orgCanAccessPostMatch(org) ? children : <Navigate to="/federation/dashboard" replace />;
}

// Platform Admin has its own, completely separate token (tactmark_admin_token)
// and its own login page (/admin/login) it is never derived from the
// client organization session (`useAuth`/`user`), so a client account can
// never reach this route no matter what flags exist on its own record.
// PlatformAdmin itself re-validates the token against the server on mount
// and bounces to /admin/login if it's missing or invalid.
function AdminPrivateRoute({ children }) {
  const hasAdminToken = !!localStorage.getItem('tactmark_admin_token');
  return hasAdminToken ? children : <Navigate to="/admin/login" />;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen bg-white">
          <Routes>
            {/* Public marketing pages no login required */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<InsightDetail />} />
            <Route path="/football-analysis-software" element={<FootballAnalysisSoftware />} />
            <Route path="/soccer-coaching-tools" element={<SoccerCoachingTools />} />
            <Route path="/soccer-analysis-software" element={<SoccerAnalysisSoftware />} />
            <Route path="/post-match-analysis-engine" element={<PostMatchAnalysisEngine />} />
            <Route path="/match-evidence-platform" element={<MatchEvidencePlatform />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/post-match" element={<ProductPostMatch />} />
            <Route path="/products/federation" element={<ProductFederation />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Navigate to="/login?mode=signup" replace />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/share/:token" element={<SharedReport />} />
            {/* Legacy share path — keep bookmarked /shared/:token links working */}
            <Route path="/shared/:token" element={<SharedReport />} />

            {/* Platform Admin fully separate auth system, own login page,
                own header, no client Navbar, no client session involved. */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminPrivateRoute><PlatformAdmin /></AdminPrivateRoute>} />
            {/* Old path kept as a redirect so any bookmarked link still lands correctly. */}
            <Route path="/platform-admin" element={<Navigate to="/admin" />} />

            {/* Authenticated client app each organization only ever sees its own data */}
            <Route path="/*" element={
              <PrivateRoute>
                <Navbar />
                <div className="pt-24">
                  <Routes>
                    <Route path="/dashboard" element={<PostMatchRoute><Dashboard /></PostMatchRoute>} />
                    <Route path="/upload" element={<PostMatchRoute><UploadMatch /></PostMatchRoute>} />
                    <Route path="/analysis/:id" element={<PostMatchRoute><AnalysisReport /></PostMatchRoute>} />
                    <Route path="/matches" element={<PostMatchRoute><Matches /></PostMatchRoute>} />
                    <Route path="/reports" element={<PostMatchRoute><Reports /></PostMatchRoute>} />
                    <Route path="/teams" element={<Teams />} />
                    <Route path="/players" element={<PostMatchRoute><Players /></PostMatchRoute>} />
                    <Route path="/patterns" element={<PostMatchRoute><Patterns /></PostMatchRoute>} />
                    <Route path="/memory" element={<PostMatchRoute><MatchMemory /></PostMatchRoute>} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/choose-product" element={<ChooseProduct />} />
                    <Route path="/subscription" element={<Navigate to="/settings" />} />

                    {/* Federation workspace */}
                    <Route path="/federation/dashboard" element={<FederationDashboard />} />
                    <Route path="/federation/programs" element={<FederationPrograms />} />
                    <Route path="/federation/submissions" element={<FederationSubmissions />} />
                    <Route path="/federation/teams" element={<Navigate to="/federation/programs" replace />} />
                    <Route path="/federation/findings" element={<FederationFindings />} />
                    <Route path="/federation/actions" element={<FederationActions />} />
                    <Route path="/federation/objectives" element={<FederationObjectives />} />
                    <Route path="/federation/reviews" element={<FederationReviews />} />
                    <Route path="/federation/reports" element={<FederationReports />} />
                    <Route path="/federation/settings" element={<FederationSettings />} />

                    {/* Preferred product URL aliases */}
                    <Route path="/app/post-match/dashboard" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/app/federation/dashboard" element={<Navigate to="/federation/dashboard" replace />} />
                    <Route path="/app/federation/*" element={<Navigate to="/federation/dashboard" replace />} />

                    <Route path="*" element={<Navigate to="/dashboard" />} />
                  </Routes>
                </div>
              </PrivateRoute>
            } />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
